import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import DomainAddOutlinedIcon from "@mui/icons-material/DomainAddOutlined";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Button from "@mui/material/Button";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import EntitiesService from "../../services/EntitiesService";
import UsersService from "../../services/UsersService";
import { toast } from "react-toastify";
import { useTheme } from "@mui/material/styles";
import IUsersData from "../../types/Users";
import { useDefaultStyles } from "../../common/styles/default";
import FooterEvent from "../../events/components/footer";
import Banner from "../../common/components/banner";
import banner from "../../assets/images/banners/event_form_banner.png";
import TitleForm from "../../common/components/titleform";
import secureLocalStorage from "react-secure-storage";
import IEntityData from "../../types/Entities";
import Skeleton from "@mui/material/Skeleton";
import CircularProgress from "@mui/material/CircularProgress";
import LoadingButton from "@mui/lab/LoadingButton";

interface FormState {
  name: string;
  cnpj: string;
  contact: string;
  owner: string;
  entityParent: string;
  addressAgent: string;
  agentVersion: string;
}

export default function FormEntityV2() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const theme = useTheme();
  const styles = useDefaultStyles();
  const { id } = useParams();

  let auth_user_id = secureLocalStorage.getItem("id");
  const role = secureLocalStorage.getItem("role")?.toString();

  const [form, setForm] = useState<FormState>({
    name: "",
    cnpj: "",
    contact: "",
    owner: "",
    entityParent: "",
    addressAgent: "",
    agentVersion: "",
  });
  const [users, setUsers] = useState<IUsersData[]>([]);
  const [entities, setEntities] = useState<IEntityData[]>([]);
  const [loading, setLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(true);
  const [entitiesLoading, setEntitiesLoading] = useState(true);
  const [entityLoading, setEntityLoading] = useState(!!id);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSelect = (name: keyof FormState) => (event: SelectChangeEvent) => {
    setForm({ ...form, [name]: event.target.value });
  };

  const validate = () => {
    if (!form.name) return t("toast_msg_formentity_name");
    if (!form.cnpj) return t("toast_msg_formentity_cnpj");
    if (form.cnpj.length < 14) return t("toast_msg_formentity_cnpj_len");
    // Exceção: não exigir entityParent se id for 1 (Entidade Hiplade)
    if (!(id === '1') && !form.entityParent) return t("toast_msg_formentity_parent_id");
    if (!form.addressAgent) return t("toast_msg_formentity_agent");
    return null;
  };

  const handleSubmit = (event?: React.FormEvent<HTMLFormElement>) => {
    event && event.preventDefault();
    const error = validate();
    if (error) {
      toast(error, { type: "error" });
      return;
    }
    setLoading(true);
    const dataEntities = {
      name: form.name,
      cnpj: form.cnpj,
      contact: form.contact,
      owner_id: !form.owner ? auth_user_id : form.owner,
      parent_id: form.entityParent ?? null,
      address_rca: form.addressAgent,
    };
    if (!id) {
      EntitiesService.store(dataEntities)
        .then((response: any) => {
          setTimeout(() => toast(response.data.Msg, { type: "success" }), 300);
          navigate("/entities/list");
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error, { type: "error" });
        })
        .finally(() => setLoading(false));
    } else {
      EntitiesService.update(id, dataEntities)
        .then((response: any) => {
          setTimeout(() => toast(response.data.Msg, { type: "success" }), 300);
          navigate("/entities/list");
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error, { type: "error" });
        })
        .finally(() => setLoading(false));
    }
  };

  useEffect(() => {
    setUsersLoading(true);
    setEntitiesLoading(true);
    UsersService.getAll(1, 0)
      .then((response: any) => setUsers(response.data))
      .catch((e: any) => {
        toast(e?.response?.data?.Error ?? e?.message, { type: "error" });
      })
      .finally(() => setUsersLoading(false));
    EntitiesService.getAll(1, 0)
      .then((response: any) => setEntities(response.data))
      .catch((e: any) => {
        toast(e?.response?.data?.Error ?? e?.message, { type: "error" });
      })
      .finally(() => setEntitiesLoading(false));
    if (id) {
      setEntityLoading(true);
      EntitiesService.get(id)
        .then((response: any) => {
          if (response) {
            setForm({
              name: response.data.name,
              cnpj: response.data.cnpj,
              contact: response.data.contact,
              owner: response.data.owner_id,
              entityParent: response.data.parent_id,
              addressAgent: response.data.address_rca,
              agentVersion: response.data.rca_version	 || "",
            });
          }
        })
        .catch((e: any) => {
          toast(e?.response?.data?.Error ?? e?.message, { type: "error" });
        })
        .finally(() => setEntityLoading(false));
    }
  }, [id]);

  // Loader global para a página inteira enquanto carrega dados essenciais
  if (usersLoading || entitiesLoading || entityLoading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', mt: 8, ml: 4 }}>
        {/* Loader global para feedback visual */}
        <CircularProgress />
        <Typography sx={{ mt: 2 }}>Carregando dados do formulário...</Typography>
        {/* Skeletons reservam espaço dos campos para evitar layout shift */}
        <Box sx={{ width: '50%', mt: 4 }}>
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
          <Skeleton variant="rectangular" height={56} sx={{ mb: 2 }} />
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Banner
        type="small"
        image={banner}
        title={
          <Box sx={{ display: "flex", alignItems: "center", gap: 5 }}>
            <DomainAddOutlinedIcon
              sx={{ width: "3em", height: "3em" }}
            />
            <Typography variant="h4" sx={{ fontWeight: "bold" }}>
              {!id
                ? t("title_page_form_register_entitie")
                : t("title_page_form_register_entitie_edit")}
            </Typography>
          </Box>
        }
      />
      <Box
        noValidate
        className={styles.container}
        component="form"
        onSubmit={handleSubmit}
        sx={{
          height: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
        }}
      >
        <Box sx={{ mb: 2, width: "50%" }}>
          <TextField
            required
            fullWidth
            id="name"
            name="name"
            label={t("input_label_name")}
            variant="outlined"
            value={form.name}
            onChange={handleChange}
          />
        </Box>
        <Box sx={{ width: "50%", mb: 2 }}>
          <TextField
            required
            fullWidth
            id="cnpj"
            name="cnpj"
            label={t("input_label_cnpj")}
            variant="outlined"
            value={form.cnpj}
            onChange={handleChange}
          />
        </Box>
        <Box sx={{ mb: 2, width: "50%", display: "flex", gap: 3 }}>
          <Box sx={{ flex: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="owner-label">{t("input_label_user")}</InputLabel>
              <Select
                labelId="owner-label"
                label={t("input_label_user")}
                value={form.owner}
                onChange={handleSelect("owner")}
                disabled={id === '1'} // Bloqueia o campo se id for 1
              >
                {users.map((row, index) => (
                  <MenuItem key={index} value={row.id}>
                    {row.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Box>
        <Box sx={{ width: "50%", mb: 2 }}>
          <FormControl fullWidth>
            <InputLabel id="entity-parent-label">
              {t("input_label_entity_parent")}
            </InputLabel>
            <Select
              labelId="entity-parent-label"
              label={t("input_label_entity_father")}
              value={form.entityParent}
              onChange={handleSelect("entityParent")}
              disabled={id === '1'} // Bloqueia o campo se id for 1
            >
              {entities &&
                entities.map((row, index) => (
                  <MenuItem key={index} value={row.id}>
                    {row.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={{ mb: 2, width: "50%" }}>
          <TextField
            fullWidth
            id="contact"
            name="contact"
            label={t("input_label_contact")}
            variant="outlined"
            value={form.contact}
            onChange={handleChange}
          />
        </Box>
        {role == "1" && (
          <Box sx={{ width: "50%", mt: 5, justifyContent: "left" }}>
            <TitleForm title={t("title_entity_config")} />
            <Box>
              {t("first_description_dialog_entity_config")}
              <strong>{t("first_description_strong_entity_config")}</strong>
              {t("second_description_dialog_entity_config")} {t("third_description_dialog_entity_config")} <strong>{t("second_description_strong_entity_config")}</strong>, {t("fourth_description_dialog_entity_config")} <strong>{t("second_description_strong_entity_config")}</strong> {t("fifith_description_strong_entity_config")}
            </Box>
            <br />
            <TextField
              fullWidth
              id="addressAgent"
              name="addressAgent"
              label={"Endereço do RCA"}
              variant="outlined"
              value={form.addressAgent}
              onChange={handleChange}
            />
            {/* Exibe o texto e o valor de agentVersion após o campo de Endereço do RCA */}
            {form.agentVersion && (
              <Typography sx={{ mt: 1 }}>
                Versão do agente: <strong>{form.agentVersion}</strong>
              </Typography>
            )}
          </Box>
        )}
      </Box>
      <FooterEvent onClickAction={handleSubmit as any} />
    </Box>
  );
}
